// Типізована обгортка H01 in-app сповіщень (notifications_router, M11,
// require_authenticated). Користувацький текст/лінк приходять як backend
// presentation — це те саме джерело, яке використовують Email і Telegram.
import { apiRequestEnvelope } from "./apiClient";

export interface Notification {
  id: string;
  type_code: string;
  channel: string;
  payload: Record<string, unknown>;
  presentation?: {
    title: string;
    description: string;
    action_label: string;
    action_url: string | null;
    occurred_at: string | null;
    occurred_at_label: string | null;
  };
  read_at: string | null;
  created_at: string | null;
}

export interface NotificationsPage {
  notifications: Notification[];
  page: number;
  perPage: number;
  total: number;
  unreadCount: number;
}

export interface NotificationsQuery {
  unreadOnly?: boolean;
  page?: number;
  perPage?: number;
}

export type NotificationTestChannel = "IN_APP" | "EMAIL" | "TELEGRAM";

function readNumber(source: unknown, ...path: string[]): number | undefined {
  let current: unknown = source;
  for (const key of path) {
    if (typeof current !== "object" || current === null) return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return typeof current === "number" ? current : undefined;
}

export function notificationText(notification: Notification): string {
  const title = notification.presentation?.title?.trim() || "Нове сповіщення";
  const detail = notification.presentation?.description?.trim() || "";
  return detail ? `${title}: ${detail}` : title;
}

export function notificationLink(notification: Notification): string | null {
  const link = notification.presentation?.action_url;
  return typeof link === "string" && link.startsWith("/") && !link.startsWith("//")
    ? link
    : null;
}

function buildQuery(query: NotificationsQuery): string {
  const params = new URLSearchParams();
  params.set("page", String(query.page ?? 1));
  params.set("per_page", String(query.perPage ?? 50));
  if (query.unreadOnly) params.set("unread_only", "true");
  return params.toString();
}

export const notificationsApi = {
  list: async (query: NotificationsQuery = {}): Promise<NotificationsPage> => {
    const { data, meta } = await apiRequestEnvelope<{ notifications: Notification[] }>(
      `/api/notifications?${buildQuery(query)}`,
    );
    return {
      notifications: data.notifications,
      page: readNumber(meta, "pagination", "page") ?? query.page ?? 1,
      perPage: readNumber(meta, "pagination", "per_page") ?? query.perPage ?? 50,
      total: readNumber(meta, "pagination", "total") ?? data.notifications.length,
      unreadCount: readNumber(meta, "unread_count") ?? 0,
    };
  },
  // Лічильник непрочитаних (для дзвоника) — мінімальний запит.
  unreadCount: async (): Promise<number> => {
    const { meta } = await apiRequestEnvelope<{ notifications: Notification[] }>(
      `/api/notifications?${buildQuery({ unreadOnly: true, perPage: 1 })}`,
    );
    return readNumber(meta, "unread_count") ?? 0;
  },
  markRead: async (id: string): Promise<void> => {
    await apiRequestEnvelope(`/api/notifications/${encodeURIComponent(id)}/mark-read`, {
      method: "POST",
    });
  },
  markAllRead: async (): Promise<void> => {
    await apiRequestEnvelope(`/api/notifications/mark-all-read`, { method: "POST" });
  },
  sendTest: async (channel: NotificationTestChannel): Promise<Notification> => {
    const { data } = await apiRequestEnvelope<{ notification: Notification }>(
      `/api/notifications/test`,
      {
        method: "POST",
        body: { channel },
      },
    );
    return data.notification;
  },
};
